import { useEffect, useRef } from "react";

/**
 * Calculates the next effective date for plan enrollment
 * Returns either the 1st or 15th of next month based on current date
 */
function getNextEffectiveDate(): string {
  const today = new Date();
  const currentDay = today.getDate();

  let targetDate: Date;

  // If we're before the 15th of current month, use next month's 1st
  // If we're on or after the 15th, use next month's 15th
  if (currentDay < 15) {
    // Set to 1st of next month
    targetDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  } else {
    // Set to 15th of next month
    targetDate = new Date(today.getFullYear(), today.getMonth() + 1, 15);
  }

  // Format as YYYY-MM-DD
  const year = targetDate.getFullYear();
  const month = String(targetDate.getMonth() + 1).padStart(2, '0');
  const day = String(targetDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Hook to automatically sync carriers from Word & Brown API on app load
 * Runs once per session (uses sessionStorage to track)
 */
export function useCarrierSync() {
  const hasSync = useRef(false);

  useEffect(() => {
    // Check if sync has already been done this session
    const syncedThisSession = sessionStorage.getItem('carriers_synced');

    if (syncedThisSession || hasSync.current) {
      return;
    }

    hasSync.current = true;

    const syncCarriers = async () => {
      try {
        const effectiveDate = getNextEffectiveDate();
        console.log(`[CarrierSync] Syncing carriers with effective date: ${effectiveDate}`);

        // Cutover: read carriers from our library (no live W&B sync on session start;
        // the daily catalog sync keeps carriers current).
        const response = await fetch(
          `/api/plan-source/carriers?state=CA&effectiveDate=${effectiveDate}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log(`[CarrierSync] Loaded ${data.carriers?.length || 0} carriers from library`);

          // Mark as synced for this session
          sessionStorage.setItem('carriers_synced', 'true');
        } else {
          console.warn('[CarrierSync] Failed to sync carriers:', response.status);
        }
      } catch (error) {
        console.error('[CarrierSync] Error syncing carriers:', error);
      }
    };

    // Run sync in the background after a short delay to not block UI
    const timeoutId = setTimeout(() => {
      syncCarriers();
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, []);
}
